import { CURRENCIES, SCALES_DERIVATIVES } from '../constnats/general';
import { REGEX_CURRENCY_SYMBOLS, REGEX_MULTIPLE_WHITESPACE } from '../constnats/regexes';

const regexVariables = [...CURRENCIES, ...SCALES_DERIVATIVES];

type CurrenciesRegexPatterns = Record<string, RegExp>;

/**
 * All regex patterns for regexVariables items.
 * Holds four patterns for each item:
 * 1. The item itself
 * 2. The item in parentheses
 * 3. "in" followed by the item
 * 4. "in" followed by the item, both in parentheses
 */
const dynamicLabelSanitizationPatterns: CurrenciesRegexPatterns = regexVariables.reduce((acc, item) => {
    /**
     * Regex pattern to match an item as a standalone word, with optional unit prefix.
     *
     * \\b[KMBT]?${item}\\b - Matches "${item}" or "K/M/B/T${item}" as complete words
     * \\b - Word boundary assertion to ensure the match starts at a word boundary
     * [KMBT]? - Matches an optional single character: K, M, B, or T (unit prefixes)
     * ${item} - Matches the exact string of the item
     * \\b - Word boundary assertion to ensure the match ends at a word boundary
     *
     * Pattern flags:
     * g - Global flag, finds all matches in the string
     * i - Case-insensitive flag, matches characters regardless of case
     */
    acc[`REGEX_${item}`] = new RegExp(`\\b[KMBT]?${item}\\b`, 'gi');

    /**
     * Regex pattern to match an item in parentheses, with optional unit prefix.
     *
     * \\([KMBT]?${item}\\) - Matches "(${item})" or "(K/M/B/T${item})"
     * \\( - Matches an opening parenthesis
     * [KMBT]? - Matches an optional single character: K, M, B, or T (unit prefixes)
     * ${item} - Matches the exact string of the item
     * \\) - Matches a closing parenthesis
     *
     * Pattern flags:
     * g - Global flag, finds all matches in the string
     * i - Case-insensitive flag, matches characters regardless of case
     */
    acc[`REGEX_${item}_IN_PARENTHESES`] = new RegExp(`\\([KMBT]?${item}\\)`, 'gi');

    /**
     * Regex pattern to match "in" followed by the item.
     * Supports optional unit prefixes (K, M, B, T) before the item.
     *
     * in\\s*[KMBT]?${item}\\b - Matches "in ${item}" or "in K/M/B/T${item}"
     * in - Matches the literal text "in"
     * \\s* - Matches zero or more whitespace characters
     * [KMBT]? - Matches an optional single character: K, M, B, or T (unit prefixes)
     * ${item} - Matches the exact string of the item
     * \\b - Word boundary assertion to ensure the match ends at a word boundary
     *
     * Pattern flags:
     * g - Global flag, finds all matches in the string
     * i - Case-insensitive flag, matches "in", "In", "IN", etc.
     */
    acc[`REGEX_IN_BEFORE_${item}`] = new RegExp(`in\\s*[KMBT]?${item}\\b`, 'gi');

    /**
     * Regex pattern to match "in" followed by the item, both in parentheses.
     * Supports optional unit prefixes (K, M, B, T) before the item.
     *
     * \\(in\\s*[KMBT]?${item}\\) - Matches "(in ${item})" or "(in K/M/B/T${item})"
     * \\( - Matches an opening parenthesis
     * in - Matches the literal text "in"
     * \\s* - Matches zero or more whitespace characters
     * [KMBT]? - Matches an optional single character: K, M, B, or T (unit prefixes)
     * ${item} - Matches the exact string of the item
     * \\) - Matches a closing parenthesis
     *
     * Pattern flags:
     * g - Global flag, finds all matches in the string
     * i - Case-insensitive flag, matches "in", "In", "IN", etc.
     */
    acc[`REGEX_IN_BEFORE_${item}_IN_PARENTHESES`] = new RegExp(`\\(in\\s*[KMBT]?${item}\\)`, 'gi');

    return acc;
}, {} as CurrenciesRegexPatterns);

/**
 * Regex pattern to match either:
 * 1. Digits that are not preceded by "IAS" or "IFRS" followed by optional whitespace and digits, OR
 * 2. Any character that is not a letter, digit, parenthesis, or percent sign
 *
 * (?<!(?:IAS|IFRS)\s*\d*)[0-9]+ - First alternative: digits not part of an IAS or IFRS pattern
 *   (?<!(?:IAS|IFRS)\s*\d*) - Negative lookbehind assertion
 *     (?:IAS|IFRS) - Non-capturing group matching either "IAS" or "IFRS"
 *     \s* - Matches zero or more whitespace characters
 *     \d* - Matches zero or more digits
 *     The entire assertion ensures the digits that follow are NOT preceded by either pattern
 *   [0-9]+ - Matches one or more digits
 *
 * | - OR operator
 *
 * [^\p{L}()%0-9] - Second alternative: non-alphanumeric, non-parentheses, non-percent characters
 *   ^ - When inside square brackets, negates the character class
 *   \p{L} - Matches any Unicode letter in any language or script
 *   () - Matches literal parenthesis characters
 *   % - Matches literal percent sign characters
 *   0-9 - Matches digit characters
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * u - Unicode flag, enables correct handling of Unicode properties like \p{L}
 *     Without this flag, Unicode property escapes won't work correctly
 */
const REGEX_NON_LETTER_AND_NUMBER_WITHOUT_SPECIAL_PREFIXES = /(?<!(?:IAS|IFRS)\s*\d*)[0-9]+|[^\p{L}()%0-9]/gu;

const REGEX_NON_LETTER_AND_NUMBER_WITHOUT_SPECIAL_PREFIXES_IN_PARENTHESES =
    /(?<!(?:IAS|IFRS)\s*\d*)\([0-9]\)+|\([^\p{L}()%0-9]\)/gu;

/**
 * Regex pattern to match currency symbols enclosed in parentheses.
 *
 * \(\p{Sc}\) - Matches any currency symbol that is enclosed in parentheses
 * \( - Matches an opening parenthesis
 * \p{Sc} - Matches any Unicode currency symbol character
 * \) - Matches a closing parenthesis
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * u - Unicode flag, enables correct handling of Unicode properties like \p{Sc}
 *     Without this flag, Unicode property escapes won't work correctly
 */
const REGEX_CURRENCY_SYMBOLS_IN_PARENTHESES = /\(\p{Sc}\)/gu;

/**
 * Regex pattern to match currency symbols followed by 'm' within parentheses.
 *
 * \(\p{Sc}m\) - Matches a currency symbol followed by 'm' enclosed in parentheses
 * \( - Matches the opening parenthesis character
 * \p{Sc} - Matches any Unicode currency symbol character
 * m - Matches the literal text "m"
 * \) - Matches the closing parenthesis character
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * u - Unicode flag, enables correct handling of Unicode properties like \p{Sc}
 *     Without this flag, Unicode property escapes won't work correctly
 */
const REGEX_CURRENCY_SYMBOLS_M_AFTER_IN_PARENTHESES = /\(\p{Sc}m\)/gu;

/**
 * Regex pattern to match "in" followed by currency symbols.
 *
 * in\s*\p{Sc} - Matches "in" followed by an optional whitespace and a currency symbol
 * in - Matches the literal text "in"
 * \s* - Matches zero or more whitespace characters
 * \p{Sc} - Matches any Unicode currency symbol character
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * i - Case-insensitive flag, matches "in", "In", "IN", etc.
 * u - Unicode flag, enables correct handling of Unicode properties like \p{Sc}
 *     Without this flag, Unicode property escapes won't work correctly
 */
const REGEX_IN_BEFORE_CURRENCY_SYMBOLS = /in\s*\p{Sc}/giu;

/**
 * Regex pattern to match "in" followed by currency symbols in parentheses.
 *
 * \(in\s*\p{Sc}\) - Matches "in" followed by an optional whitespace and a currency symbol in parentheses
 * in - Matches the literal text "in"
 * \s* - Matches zero or more whitespace characters
 * \( - Matches an opening parenthesis
 * \p{Sc} - Matches any Unicode currency symbol character
 * \) - Matches a closing parenthesis
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * i - Case-insensitive flag, matches "in", "In", "IN", etc.
 * u - Unicode flag, enables correct handling of Unicode properties like \p{Sc}
 *     Without this flag, Unicode property escapes won't work correctly
 */
const REGEX_IN_BEFORE_CURRENCY_SYMBOLS_IN_PARENTHESES = /\(in\s*\p{Sc}\)/giu;

const CURRENCY_SYMBOLS_ALL_FORMATS =
    `${REGEX_CURRENCY_SYMBOLS.source}|` +
    `${REGEX_CURRENCY_SYMBOLS_IN_PARENTHESES.source}|` +
    `${REGEX_IN_BEFORE_CURRENCY_SYMBOLS.source}|` +
    `${REGEX_IN_BEFORE_CURRENCY_SYMBOLS_IN_PARENTHESES.source}|` +
    `${REGEX_CURRENCY_SYMBOLS_M_AFTER_IN_PARENTHESES.source}`;

/**
 * Regex pattern to match a standalone letter with whitespace.
 *
 * \s+[A-Z]\s* - Matches a standalone letter surrounded by whitespace
 * \s+ - Matches one or more whitespace characters (spaces, tabs, newlines)
 * [AB] - Matches exactly one character that is in the range of 'A' to 'Z'
 * \s* - Matches zero or more whitespace characters
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * m - Multiline flag, affects ^ and $ anchors
 */
const REGEX_STANDALONE_LETTER = /\s+[A-Z]\s*/gm;

/**
 * Regex pattern to match a standalone letter in parentheses with whitespace.
 *
 * \s+\([A-Z]\)\s* - Matches a standalone letter enclosed in parentheses surrounded by whitespace
 * \s+ - Matches one or more whitespace characters (spaces, tabs, newlines)
 * \( - Matches a literal opening parenthesis character
 * [A-Z] - Matches exactly one character that is in the range of 'A' to 'Z'
 * \) - Matches a literal closing parenthesis character
 * \s* - Matches zero or more whitespace characters
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * m - Multiline flag, affects ^ and $ anchors
 */
const REGEX_STANDALONE_LETTER_IN_PARENTHESES_PRECEDED_BY_WHITESPACE = /\s+\([A-Z]\)\s*/gm;

/**
 * Regex pattern to match a standalone letter in parentheses.
 *
 * \([A-Z]\) - Matches a standalone letter enclosed in parentheses surrounded by whitespace
 * \( - Matches a literal opening parenthesis character
 * [A-Z] - Matches exactly one character that is in the range of 'A' to 'Z'
 * \) - Matches a literal closing parenthesis character
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * i - Case-insensitive flag, matches "in", "In", "IN", etc.
 * m - Multiline flag, affects ^ and $ anchors
 */
const REGEX_STANDALONE_LETTER_IN_PARENTHESES = /\([A-Z]\)/gim;

/**
 * Regex pattern to match the superscript versions of "(a)", "(b)", "(c)", etc..
 *
 * ⁽[ᵃ-ᶻ]⁾
 *   ⁽ - Superscript left parenthesis (U+207D)
 *   [ᵃ-ᶻ] - Includes lowercase superscript letters: ᵃ-ᶻ (range)
 *   ⁾ - Superscript right parenthesis (U+207E)
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * u - Unicode flag (required for proper Unicode character handling)
 */
const REGEX_SUPERSCRIPT_LETTERS_IN_PARENTHESES = /⁽[ᵃ-ᶻ]⁾/gu;

/**
 * Regex pattern to match all Unicode superscript numbers.
 *
 * [\u00B9\u00B2\u00B3\u2070\u2074-\u2079] - Matches any Unicode superscript number character
 * \u00B9 - Matches superscript 1 (¹)
 * \u00B2 - Matches superscript 2 (²)
 * \u00B3 - Matches superscript 3 (³)
 * \u2070 - Matches superscript 0 (⁰)
 * \u2074-\u2079 - Matches superscript numbers 4-9 (⁴⁵⁶⁷⁸⁹)
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 * u - Unicode flag, enables correct handling of Unicode characters
 */
const REGEX_SUPERSCRIPT_NUMBERS = /[\u00B9\u00B2\u00B3\u2070\u2074-\u2079]/gu;

/**
 * Regex pattern that matches lowercase letters or numbers followed by a closing parenthesis.
 * Commonly used for matching list markers like "a)", "b)", "1)", "2)", etc.
 *
 * [a-z0-9]\)
 *   [a-z0-9] - Character class matching:
 *     a-z - Any lowercase letter from 'a' to 'z'
 *     0-9 - Any digit from '0' to '9'
 *   \) - Literal closing parenthesis
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 */
const REGEX_LETTER_OR_NUMBER_BULLET = /[a-z0-9]\)/g;

/**
 * Regex pattern that matches lowercase letters or numbers preceded by a whitespace and followed by a closing parenthesis.
 * Commonly used for matching list markers like "a)", "b)", "1)", "2)", etc.
 *
 * \s+[a-z0-9]\)
 *   [a-z0-9] - Character class matching:
 *     a-z - Any lowercase letter from 'a' to 'z'
 *     0-9 - Any digit from '0' to '9'
 *   \) - Literal closing parenthesis
 *   \s+ - Matches one or more whitespace characters (spaces, tabs, newlines)
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 */
const REGEX_LETTER_OR_NUMBER_BULLET_PRECEDED_BY_WHITESPACE = /\s+[a-z0-9]\)/g;

/**
 * Regex pattern to match "Note" followed by any characters, all within parentheses.
 *
 * \(Note.*\) - Matches "(Note...)" where "..." is any text
 * \( - Matches an opening parenthesis
 * Note - Matches the literal text "Note"
 * .* - Matches zero or more of any character (except newline by default)
 * \) - Matches a closing parenthesis
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 */
const REGEX_PARENTHESIZED_NOTE = /\(Note.*\)/gi;

/**
 * Regex pattern that matches lowercase letters or numbers preceded by a whitespace and an opening parenthesis.
 * Commonly used for matching list markers like "a)", "b)", "1)", "2)", etc.
 *
 * \s+\([a-z0-9]
 *   [a-z0-9] - Character class matching:
 *     a-z - Any lowercase letter from 'a' to 'z'
 *     0-9 - Any digit from '0' to '9'
 *   \( - Literal opening parenthesis
 *   \s+ - Matches one or more whitespace characters (spaces, tabs, newlines)
 *
 * Pattern flags:
 * g - Global flag, finds all matches in the string
 */
const REGEX_LETTER_OR_NUMBER_REVERSE_BULLET_PRECEDED_BY_WHITESPACE = /\s+\([a-z0-9]/g;

export const REGEX_ALL_DYNAMIC_PATTERNS = Object.values(dynamicLabelSanitizationPatterns)
    .map((rp) => rp.source)
    .join('|');

export const REGEX_CLEAN_BEGINNING = new RegExp(
    `^(${REGEX_LETTER_OR_NUMBER_BULLET.source}|${CURRENCY_SYMBOLS_ALL_FORMATS}|${REGEX_ALL_DYNAMIC_PATTERNS}|${REGEX_NON_LETTER_AND_NUMBER_WITHOUT_SPECIAL_PREFIXES.source})+`,
    'gmui'
);

export const REGEX_CLEAN_END = new RegExp(
    `(${REGEX_SUPERSCRIPT_LETTERS_IN_PARENTHESES.source}|${REGEX_STANDALONE_LETTER_IN_PARENTHESES.source}|${REGEX_LETTER_OR_NUMBER_REVERSE_BULLET_PRECEDED_BY_WHITESPACE.source}|${REGEX_LETTER_OR_NUMBER_BULLET_PRECEDED_BY_WHITESPACE.source}|${CURRENCY_SYMBOLS_ALL_FORMATS}|${REGEX_ALL_DYNAMIC_PATTERNS}|${REGEX_NON_LETTER_AND_NUMBER_WITHOUT_SPECIAL_PREFIXES.source}|${REGEX_NON_LETTER_AND_NUMBER_WITHOUT_SPECIAL_PREFIXES_IN_PARENTHESES.source}|${REGEX_STANDALONE_LETTER.source}|${REGEX_STANDALONE_LETTER_IN_PARENTHESES_PRECEDED_BY_WHITESPACE.source}|${REGEX_PARENTHESIZED_NOTE.source})+$`,
    'gmui'
);

export const REGEX_CLEAN_EVERYWHERE = new RegExp(
    `(${REGEX_SUPERSCRIPT_NUMBERS.source}|${REGEX_ALL_DYNAMIC_PATTERNS}|${CURRENCY_SYMBOLS_ALL_FORMATS})`,
    'gmui'
);

// Note: The order of the regexes is important.
export const REGEXES_TO_MATCH = [
    REGEX_CLEAN_BEGINNING,
    REGEX_CLEAN_END,
    REGEX_CLEAN_EVERYWHERE,
    REGEX_MULTIPLE_WHITESPACE,
];

export function sanitizeLabel(label: string): string {
    // Replace unwanted elements from string
    let result = label;
    for (const REGEX of REGEXES_TO_MATCH) {
        result = result.replaceAll(REGEX, ' ');
    }
    return result.trim();
}
